import React from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { RouterErrorBoundary } from "@/components/RouterErrorBoundary";
import AuthGuard from "@/components/AuthGuard";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";
import { MobilePWAPrompt } from "@/components/MobilePWAPrompt";
import { PWAInstallButton } from "@/components/PWAInstallButton";
import { RouteSEO } from "@/components/RouteSEO";
import { SkipLinks } from "@/components/accessibility/SkipLinks";
import { AppLayout } from "@/components/AppLayout";
import { logger } from '@/utils/logger';

import { useGDPR } from '@/hooks/useGDPR';

// Lazy loading des pages pour optimiser le bundle avec retry automatique
const createLazyComponent = (importFn: () => Promise<any>, componentName: string) => {
  return lazy(() => 
    importFn().catch((error) => {
      logger.warn(`Failed to load ${componentName}:`, String(error));
      throw error;
    })
  );
};

const Index = createLazyComponent(() => import("@/pages/Index"), "Index");
const Auth = createLazyComponent(() => import("@/pages/Auth"), "Auth");
const AuthConfirm = createLazyComponent(() => import("@/pages/AuthConfirm"), "AuthConfirm");
const Dashboard = createLazyComponent(() => import("@/pages/Dashboard"), "Dashboard");
const Study = createLazyComponent(() => import("@/pages/Study"), "Study");
const Chat = createLazyComponent(() => import("@/pages/Chat"), "Chat");
const Community = createLazyComponent(() => import("@/pages/Community"), "Community");
const Flashcards = createLazyComponent(() => import("@/pages/Flashcards"), "Flashcards");
const RevisionSheets = createLazyComponent(() => import("@/pages/RevisionSheets"), "RevisionSheets");
const Calculator = createLazyComponent(() => import("@/pages/Calculator"), "Calculator");
const Checklists = createLazyComponent(() => import("@/pages/Checklists"), "Checklists");
const Guidelines = createLazyComponent(() => import("@/pages/Guidelines"), "Guidelines");
const Quiz = createLazyComponent(() => import("@/pages/Quiz"), "Quiz");
const DailyQuiz = createLazyComponent(() => import("@/pages/DailyQuiz"), "DailyQuiz");
const Settings = createLazyComponent(() => import("@/pages/Settings"), "Settings");
const Scores = createLazyComponent(() => import("@/pages/Scores"), "Scores");
const Medications = createLazyComponent(() => import("@/pages/Medications"), "Medications");
const Coding = createLazyComponent(() => import("@/pages/Coding"), "Coding");
const RSSAggregator = createLazyComponent(() => import("@/pages/RSSAggregator"), "RSSAggregator");
const Statistiques = createLazyComponent(() => import("@/pages/Statistiques"), "Statistiques");
const Consultations = createLazyComponent(() => import("@/pages/Consultations"), "Consultations");
const NouveauPatient = createLazyComponent(() => import("@/pages/NouveauPatient"), "NouveauPatient");
const PatientDetails = createLazyComponent(() => import("@/pages/PatientDetails"), "PatientDetails");
const ConsultationEditor = createLazyComponent(() => import("@/pages/ConsultationEditor"), "ConsultationEditor");
const CoursesIndex = createLazyComponent(() => import("@/pages/CoursesIndex"), "CoursesIndex");
const CourseDetail = createLazyComponent(() => import("@/pages/CourseDetail"), "CourseDetail");
const UrgentisteMasque = createLazyComponent(() => import("@/pages/UrgentisteMasque"), "UrgentisteMasque");
const UrgentisteMasqueCas = createLazyComponent(() => import("@/pages/UrgentisteMasqueCas"), "UrgentisteMasqueCas");
const RevisionExpress = createLazyComponent(() => import("@/pages/RevisionExpress"), "RevisionExpress");
const Challenges = createLazyComponent(() => import("@/pages/Challenges"), "Challenges");
const Achievements = createLazyComponent(() => import("@/pages/Achievements"), "Achievements");
const Pricing = createLazyComponent(() => import("@/pages/Pricing"), "Pricing");
const Subscribe = createLazyComponent(() => import("@/pages/Subscribe"), "Subscribe");
const HallOfFame = createLazyComponent(() => import("@/pages/HallOfFame"), "HallOfFame");
const SharedDeck = createLazyComponent(() => import("@/pages/SharedDeck"), "SharedDeck");
const SharedSheet = createLazyComponent(() => import("@/pages/SharedSheet"), "SharedSheet");
const SharedQuiz = createLazyComponent(() => import("@/pages/SharedQuiz"), "SharedQuiz");
const SharedCase = createLazyComponent(() => import("@/pages/SharedCase"), "SharedCase");
const PublicProfile = createLazyComponent(() => import("@/pages/PublicProfile"), "PublicProfile");
const NotFound = createLazyComponent(() => import("@/pages/NotFound"), "NotFound");
const PublicCalculatorIndex = createLazyComponent(() => import("@/pages/PublicCalculatorIndex"), "PublicCalculatorIndex");
const PublicCalculator = createLazyComponent(() => import("@/pages/PublicCalculator"), "PublicCalculator");
const Blog = createLazyComponent(() => import("@/pages/Blog"), "Blog");
const BlogPost = createLazyComponent(() => import("@/pages/BlogPost"), "BlogPost");
const CGU = createLazyComponent(() => import("@/pages/CGU"), "CGU");
const PrivacyPage = createLazyComponent(() => import("@/pages/Privacy"), "Privacy");
const MentionsLegales = createLazyComponent(() => import("@/pages/MentionsLegales"), "MentionsLegales");
const ArticlesIndex = createLazyComponent(() => import("@/pages/ArticlesIndex"), "ArticlesIndex");
const ArticlePage = createLazyComponent(() => import("@/pages/ArticlePage"), "ArticlePage");
const Features = createLazyComponent(() => import("@/pages/Features"), "Features");
const AboutDoctor = createLazyComponent(() => import("@/pages/AboutDoctor"), "AboutDoctor");
const Support = createLazyComponent(() => import("@/pages/Support"), "Support");

// Admin pages
const AdminLayout = createLazyComponent(() => import("@/pages/admin/AdminLayout"), "AdminLayout");
const AdminDashboard = createLazyComponent(() => import("@/pages/admin/Dashboard"), "AdminDashboard");
const AdminEngagement = createLazyComponent(() => import("@/pages/admin/Engagement"), "AdminEngagement");
const AdminUsageStats = createLazyComponent(() => import("@/pages/admin/UsageStats"), "AdminUsageStats");
const AdminAIPrompts = createLazyComponent(() => import("@/pages/admin/AIPrompts"), "AdminAIPrompts");
const AdminNotifications = createLazyComponent(() => import("@/pages/admin/Notifications"), "AdminNotifications");
const AdminUsers = createLazyComponent(() => import("@/pages/admin/Users"), "AdminUsers");
const AdminPushDebug = createLazyComponent(() => import("@/pages/admin/PushDebug"), "AdminPushDebug");
const AdminPushTest = createLazyComponent(() => import("@/pages/admin/PushTest"), "AdminPushTest");
const AdminSystemLogs = createLazyComponent(() => import("@/pages/admin/SystemLogs"), "AdminSystemLogs");
const AdminSendSamples = createLazyComponent(() => import("@/pages/admin/SendSamples"), "AdminSendSamples");
const AdminExpiredCleanup = createLazyComponent(() => import("@/pages/admin/ExpiredUsersCleanup"), "AdminExpiredCleanup");
const AdminXPBadgeMonitor = createLazyComponent(() => import("@/pages/admin/XPBadgeMonitor"), "AdminXPBadgeMonitor");
const AdminCreateChallenge = createLazyComponent(() => import("@/pages/admin/CreateChallenge"), "AdminCreateChallenge");
const AdminFeedbacks = createLazyComponent(() => import("@/pages/admin/Feedbacks"), "AdminFeedbacks");
const AdminSocialPosts = createLazyComponent(() => import("@/pages/admin/SocialPosts"), "AdminSocialPosts");

const AppWithI18n: React.FC = () => {
  const { t } = useTranslation();
  useGDPR();
  // useAuthMonitor removed: dev-only monitoring now handled by Sentry in production

  return (
    <BrowserRouter>
      <RouterErrorBoundary>
        <RouteSEO />
        <SkipLinks />
        <Suspense fallback={
          <div className="min-h-screen bg-background">
            <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
              <div className="container mx-auto flex h-16 items-center px-4">
                <img src="/logo-sclepios.png" alt="Sclepios" className="h-8" />
              </div>
            </header>
            <div className="flex items-center justify-center pt-32">
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">{t('common.loadingPage')}</p>
              </div>
            </div>
          </div>
        }>
          <AppLayout>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/confirm" element={<AuthConfirm />} />
              <Route path="/dashboard" element={<AuthGuard><Dashboard /></AuthGuard>} />
              <Route path="/chat" element={<AuthGuard><Chat /></AuthGuard>} />
              <Route path="/study" element={<AuthGuard><Study /></AuthGuard>} />
              <Route path="/community" element={<AuthGuard><Community /></AuthGuard>} />
              <Route path="/flashcards" element={<AuthGuard><Flashcards /></AuthGuard>} />
              <Route path="/revision-sheets" element={<AuthGuard><RevisionSheets /></AuthGuard>} />
              <Route path="/calculator" element={<Calculator />} />
              <Route path="/calculators" element={<Navigate to="/calculator" replace />} />
              <Route path="/checklists" element={<AuthGuard><Checklists /></AuthGuard>} />
              <Route path="/guidelines" element={<AuthGuard><Guidelines /></AuthGuard>} />
              <Route path="/quiz" element={<AuthGuard><Quiz /></AuthGuard>} />
              <Route path="/daily-quiz" element={<AuthGuard><DailyQuiz /></AuthGuard>} />
              <Route path="/scores" element={<AuthGuard><Scores /></AuthGuard>} />
              <Route path="/medications" element={<AuthGuard><Medications /></AuthGuard>} />
              <Route path="/coding" element={<AuthGuard><Coding /></AuthGuard>} />
              <Route path="/rss-aggregator" element={<AuthGuard><RSSAggregator /></AuthGuard>} />
              <Route path="/statistiques" element={<AuthGuard><Statistiques /></AuthGuard>} />
              <Route path="/settings" element={<AuthGuard><Settings /></AuthGuard>} />
              <Route path="/consultations" element={<AuthGuard requiredRole="admin"><Consultations /></AuthGuard>} />
              <Route path="/consultations/nouveau-patient" element={<AuthGuard requiredRole="admin"><NouveauPatient /></AuthGuard>} />
              <Route path="/consultations/patient/:patientId" element={<AuthGuard requiredRole="admin"><PatientDetails /></AuthGuard>} />
              <Route path="/consultations/patient/:patientId/nouvelle" element={<AuthGuard requiredRole="admin"><ConsultationEditor /></AuthGuard>} />
              <Route path="/consultations/consultation/:consultationId" element={<AuthGuard requiredRole="admin"><ConsultationEditor /></AuthGuard>} />
              <Route path="/cours" element={<AuthGuard><CoursesIndex /></AuthGuard>} />
              <Route path="/cours/:slug" element={<AuthGuard><CourseDetail /></AuthGuard>} />
              <Route path="/urgentiste-masque" element={<AuthGuard><UrgentisteMasque /></AuthGuard>} />
              <Route path="/urgentiste-masque/cas/:casId" element={<AuthGuard><UrgentisteMasqueCas /></AuthGuard>} />
              <Route path="/revision-express" element={<AuthGuard><RevisionExpress /></AuthGuard>} />
              <Route path="/challenges" element={<AuthGuard><Challenges /></AuthGuard>} />
              <Route path="/achievements" element={<AuthGuard><Achievements /></AuthGuard>} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/subscribe" element={<Subscribe />} />
              <Route path="/hall-of-fame" element={<AuthGuard><HallOfFame /></AuthGuard>} />
              <Route path="/shared/deck/:id" element={<SharedDeck />} />
              <Route path="/shared/sheet/:id" element={<SharedSheet />} />
              <Route path="/shared/quiz/:id" element={<SharedQuiz />} />
              <Route path="/shared/case/:id" element={<SharedCase />} />
              <Route path="/profil/:userId" element={<PublicProfile />} />
              
              {/* Public SEO pages */}
              <Route path="/calculateurs" element={<PublicCalculatorIndex />} />
              <Route path="/calculateur/:slug" element={<PublicCalculator />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/cgu" element={<CGU />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/mentions-legales" element={<MentionsLegales />} />
              <Route path="/legal" element={<MentionsLegales />} />
              <Route path="/articles" element={<ArticlesIndex />} />
              <Route path="/articles/:slug" element={<ArticlePage />} />
              <Route path="/features" element={<Features />} />
              <Route path="/a-propos" element={<AboutDoctor />} />
              <Route path="/support" element={<Support />} />
              
              {/* Admin routes */}
              <Route path="/admin" element={<AuthGuard><AdminLayout /></AuthGuard>}>
                <Route index element={<AdminDashboard />} />
                <Route path="engagement" element={<AdminEngagement />} />
                <Route path="usage-stats" element={<AdminUsageStats />} />
                <Route path="ai-prompts" element={<AdminAIPrompts />} />
                <Route path="notifications" element={<AdminNotifications />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="system-logs" element={<AdminSystemLogs />} />
                <Route path="push-debug" element={<AdminPushDebug />} />
                <Route path="push-test" element={<AdminPushTest />} />
                <Route path="send-samples" element={<AdminSendSamples />} />
                <Route path="expired-cleanup" element={<AdminExpiredCleanup />} />
                <Route path="xp-badge-monitor" element={<AdminXPBadgeMonitor />} />
                <Route path="create-challenge" element={<AdminCreateChallenge />} />
                <Route path="feedbacks" element={<AdminFeedbacks />} />
                <Route path="social-posts" element={<AdminSocialPosts />} />
                <Route path="mailing" element={<Navigate to="/admin/notifications" replace />} />
                <Route path="xp-badges" element={<Navigate to="/admin/xp-badge-monitor" replace />} />
              </Route>
              
              {/* URL aliases / redirects */}
              <Route path="/forum" element={<Navigate to="/community" replace />} />
              <Route path="/fiches" element={<Navigate to="/revision-sheets" replace />} />
              <Route path="/clinical-cases" element={<Navigate to="/urgentiste-masque" replace />} />
              <Route path="/defis" element={<Navigate to="/challenges" replace />} />
              <Route path="/profile" element={<Navigate to="/settings" replace />} />
              <Route path="/planning" element={<Navigate to="/flashcards" replace />} />
              <Route path="/abonnement" element={<Navigate to="/pricing" replace />} />
              <Route path="/courses" element={<Navigate to="/cours" replace />} />
              <Route path="/referral" element={<Navigate to="/dashboard" replace />} />
              <Route path="/faq" element={<Navigate to="/pricing" replace />} />
              <Route path="/roadmap" element={<Navigate to="/features" replace />} />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <PWAInstallPrompt />
            <MobilePWAPrompt />
            <PWAInstallButton />
          </AppLayout>
        </Suspense>
      </RouterErrorBoundary>
    </BrowserRouter>
  );
};

export default AppWithI18n;
